import type { HealthSummary } from '../api/types';
import { units } from '../stores/units.svelte';
import { KG_PER_POUND, weightText } from './units';
import { shiftDay, today, weekRange } from './insights';
import { decimal, integer, locale, month, monthDay, t, time, weekday } from '../i18n/index.ts';

// Health history's data and wording (spec 007 FR-018, 007 FR-019, 007 FR-020, 007 FR-021, 007 FR-022), as the
// server's /me/health endpoints give it.

export type HealthTab = 'day' | 'week' | 'month' | 'year';
export type SleepStage = 'awake' | 'light' | 'deep' | 'rem' | 'asleep';
export type ReadingKind = 'weight' | 'body_fat' | 'blood_pressure' | 'spo2' | 'hrv' | 'respiratory_rate' | 'body_temp';

export interface Stage {
  stage: SleepStage;
  from: string;
  to: string;
}

export interface HealthSleep {
  started_at: string;
  ended_at: string;
  asleep_s: number;
  stages: Stage[];
  source: string;
}

export interface HrHour {
  hour: number;
  min: number;
  avg: number;
  max: number;
  source: string;
}

export interface HealthReading {
  kind: ReadingKind;
  value: number;
  value2?: number;
  at: string;
  source: string;
}

export interface StepHour {
  hour: number;
  steps: number;
}

// HealthDay is one local day (007 FR-019); a measure without data is absent.
export interface HealthDay {
  date: string;
  resting_hr?: number;
  steps?: number;
  hr_hours: HrHour[];
  sleep: HealthSleep[];
  readings: HealthReading[];
  step_hours: StepHour[];
  typical_step_hours: StepHour[];
  typical_steps?: number;
  typical_asleep_s?: number;
}

// HealthBucket is one bar of a period's charts; a field is absent when no day
// in it has that measure.
export interface HealthBucket {
  from: string;
  to: string;
  steps?: number;
  asleep_s?: number;
  deep_s?: number;
  avg_hr?: number;
  resting_hr?: number;
}

export interface HealthAverage {
  value: number;
  typical?: number;
}

export interface ReadingCard {
  kind: ReadingKind;
  unit: string;
  value: number;
  value2?: number;
  buckets: { from: string; value: number; value2?: number }[];
  // trend and trend_change are weight's smoothed trend at each bucket's last
  // weigh-in and its change over the period in kg; range is heart rate
  // variability's normal range (spec 007 FR-103, 007 FR-105).
  trend?: { from: string; value: number }[];
  trend_change?: number;
  range?: { low: number; high: number; latest: number; latest_vs: 'below' | 'within' | 'above'; kind: 'sdnn' | 'rmssd' };
}

// HealthPeriod is a week, month, or year (007 FR-020); a card without data is absent.
export interface HealthPeriod {
  period: 'week' | 'month' | 'year';
  from: string;
  to: string;
  bucket: 'day' | 'week' | 'month';
  buckets: HealthBucket[];
  steps?: HealthAverage;
  // The spreads are the standard deviations of fall-asleep and wake-up times
  // in minutes, from 3 nights (spec 007 FR-104).
  sleep?: { asleep_s: HealthAverage; deep_s: number; fall_asleep: string; wake_up: string; fall_asleep_spread_min?: number; wake_up_spread_min?: number };
  heart?: { avg_hr?: number; resting_hr?: number };
  readings: ReadingCard[];
  // daily are resting heart rate and HRV by day from 6 days before the
  // period, for their 7-day averages (spec 010 FR-033).
  daily: { date: string; resting_hr?: number; hrv?: number; hrv_kind?: 'sdnn' | 'rmssd' }[];
}

// HealthSettings are the member's own keeping and the server's, 0 meaning
// the server's and until deleted respectively (007 FR-023, 007 FR-024).
export interface HealthSettings {
  health_days: number;
  server_days: number;
}

// Each measure's theme color.
export const STEPS_COLOR = 'var(--theme-green)';
export const SLEEP_COLOR = 'var(--theme-blue)';
export const HEART_COLOR = 'var(--theme-mauve)';
export const BODY_COLOR = 'var(--theme-peach)';
export const VITALS_COLOR = 'var(--theme-teal)';

export const BODY_KINDS: ReadingKind[] = ['weight', 'body_fat', 'blood_pressure'];
export const VITAL_KINDS: ReadingKind[] = ['spo2', 'hrv', 'respiratory_rate', 'body_temp'];

// readingName is a reading kind's name.
export function readingName(kind: ReadingKind): string {
  return t('healthReadingName', { kind });
}

// READING_NAMES reads each name from the catalog when asked, so it follows
// the language set after this module loads.
export const READING_NAMES: Record<ReadingKind, string> = {
  get weight() { return readingName('weight'); },
  get body_fat() { return readingName('body_fat'); },
  get blood_pressure() { return readingName('blood_pressure'); },
  get spo2() { return readingName('spo2'); },
  get hrv() { return readingName('hrv'); },
  get respiratory_rate() { return readingName('respiratory_rate'); },
  get body_temp() { return readingName('body_temp'); },
};

// STAGES are the sleep stages in the order the legend lists them. Deep is
// the full blue and light the same blue faded, as the period charts draw
// them; REM and awake take other hues so they read apart in either theme.
// Each name is read from the catalog when asked.
export const STAGES: { stage: SleepStage; name: string; color: string }[] = [
  { stage: 'deep', get name() { return stageName('deep'); }, color: 'var(--theme-blue)' },
  { stage: 'light', get name() { return stageName('light'); }, color: 'color-mix(in srgb, var(--theme-blue) 45%, transparent)' },
  { stage: 'rem', get name() { return stageName('rem'); }, color: 'var(--theme-mauve)' },
  { stage: 'asleep', get name() { return stageName('asleep'); }, color: 'var(--theme-sapphire)' },
  { stage: 'awake', get name() { return stageName('awake'); }, color: 'var(--theme-peach)' },
];

// stageName is a sleep stage's name.
export function stageName(stage: SleepStage): string {
  return t('healthStage', { stage });
}

// hourLabel is an hour of the day alone in the viewer's clock, "6 PM" or "18".
export function hourLabel(h: number): string {
  return new Intl.DateTimeFormat(locale(), { hour: 'numeric' }).format(new Date(2000, 0, 1, h)).replace(/\u202f/g, ' ');
}

// sleepTime is "7h 5m", hours shown even at none, as the Pebble app writes it.
export function sleepTime(seconds: number): string {
  const m = Math.round(seconds / 60);
  return t('healthSleepTime', { hours: integer(Math.floor(m / 60)), minutes: integer(m % 60) });
}

// stageSeconds totals a set of sessions' time in one stage.
export function stageSeconds(sleep: HealthSleep[], stage: SleepStage): number {
  let s = 0;
  for (const n of sleep) {
    for (const st of n.stages) {
      if (st.stage === stage) s += (Date.parse(st.to) - Date.parse(st.from)) / 1000;
    }
  }
  return s;
}

// hhmmClock writes the server's "22:54" in the viewer's clock, "10:54 PM".
export function hhmmClock(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  return time(new Date(2000, 0, 1, h, m));
}

// readingNumber is a reading in the unit the viewer chose (010 FR-034):
// weight in kilograms, pounds, or stones as a decimal, which charts plot,
// and temperature in Celsius or Fahrenheit.
export function readingNumber(kind: ReadingKind, v: number): number {
  if (kind === 'weight') {
    const w = units().weight;
    return w === 'kg' ? v : w === 'lb' ? v / KG_PER_POUND : v / KG_PER_POUND / 14;
  }
  if (kind === 'body_temp' && units().temperature === 'f') return v * 1.8 + 32;
  return v;
}

// readingUnit is the unit readingNumber gives, in words.
export function readingUnit(kind: ReadingKind): string {
  switch (kind) {
    case 'weight': return t('healthUnit', { unit: units().weight });
    case 'body_temp': return t('healthUnit', { unit: units().temperature === 'f' ? 'f' : 'c' });
    case 'body_fat':
    case 'spo2': return t('healthUnit', { unit: 'percent' });
    case 'blood_pressure': return t('healthUnit', { unit: 'mmhg' });
    case 'hrv': return t('healthUnit', { unit: 'ms' });
    case 'respiratory_rate': return t('healthUnit', { unit: 'breaths' });
  }
}

// withUnit writes a number already formatted with kind's unit.
function withUnit(kind: ReadingKind, text: string): string {
  if (kind === 'body_fat' || kind === 'spo2') return t('healthPercent', { value: text });
  return t('healthValueUnit', { value: text, unit: readingUnit(kind) });
}

// shownValue writes a number readingNumber gave with its unit; a weight in
// stones is "11 st 4 lb".
export function shownValue(kind: ReadingKind, n: number): string {
  if (kind === 'weight' && units().weight === 'st') return weightText(n * 14 * KG_PER_POUND, 'st');
  const text = kind === 'weight' || kind === 'body_temp' || kind === 'body_fat' || kind === 'respiratory_rate' ? decimal(n, 1) : integer(Math.round(n));
  return withUnit(kind, text);
}

// weightChange is a change in weight in kilograms or pounds, the unit in
// words; under stones it reads in pounds, since a change is rarely a whole
// stone.
export function weightChange(kg: number): { value: number; unit: string } {
  const w = units().weight;
  return w === 'kg' ? { value: kg, unit: t('healthUnit', { unit: 'kg' }) } : { value: kg / KG_PER_POUND, unit: t('healthUnit', { unit: 'lb' }) };
}

// readingValue writes a reading with its unit, "72.4 kg", "118/76 mmHg", or "97%".
export function readingValue(kind: ReadingKind, value: number, value2?: number): string {
  const n = readingNumber(kind, value);
  switch (kind) {
    case 'blood_pressure':
      if (value2 == null) return withUnit(kind, integer(Math.round(n)));
      return t('healthBloodPressure', { systolic: integer(Math.round(n)), diastolic: integer(Math.round(value2)), unit: readingUnit(kind) });
    case 'spo2':
    case 'hrv': return withUnit(kind, integer(Math.round(n)));
    case 'weight': return shownValue(kind, n);
  }
  return withUnit(kind, decimal(n, 1));
}

// weekFirst is the first day of the week holding day, weeks starting on
// weekStart (0 Sunday).
export function weekFirst(day: string, weekStart: number): string {
  const back = (new Date(`${day}T12:00:00`).getDay() - weekStart + 7) % 7;
  return shiftDay(day, -back);
}

// periodStart is the first day of the tab's period holding day.
export function periodStart(tab: HealthTab, day: string, weekStart: number): string {
  switch (tab) {
    case 'week': return weekFirst(day, weekStart);
    case 'month': return `${day.slice(0, 7)}-01`;
    case 'year': return `${day.slice(0, 4)}-01-01`;
  }
  return day;
}

// shiftPeriod moves day to the same tab's period n before or after, landing
// on that period's first day for a week, month, or year.
export function shiftPeriod(tab: HealthTab, day: string, n: number, weekStart: number): string {
  const first = periodStart(tab, day, weekStart);
  const [y, m] = first.split('-').map(Number);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  switch (tab) {
    case 'day': return shiftDay(day, n);
    case 'week': return shiftDay(first, 7 * n);
    case 'month': return iso(new Date(y, m - 1 + n, 1));
    case 'year': return `${y + n}-01-01`;
  }
}

// hasNext is whether the period after day's has begun, so the member cannot
// step into the future.
export function hasNext(tab: HealthTab, day: string, weekStart: number): boolean {
  return shiftPeriod(tab, day, 1, weekStart) <= today();
}

// periodLabel names the period: "Thu, Sep 24", "Sep 21 – 27", "September 2026", or "2026".
export function periodLabel(tab: HealthTab, day: string, weekStart: number): string {
  const d = new Date(`${day}T12:00:00`);
  switch (tab) {
    case 'day': return new Intl.DateTimeFormat(locale(), { weekday: 'short', month: 'short', day: 'numeric' }).format(d);
    case 'week': return weekRange(weekFirst(day, weekStart));
    case 'month': return new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric' }).format(d);
    case 'year': return String(d.getFullYear());
  }
}

// bucketLabel is a bar's short label under a chart: "Mon", "Sep 1 – 7", or "Sep".
export function bucketLabel(bucket: HealthPeriod['bucket'], from: string, to: string): string {
  const a = new Date(`${from}T12:00:00`);
  if (bucket === 'day') return weekday(a, true);
  if (bucket === 'month') return month(a, true);
  const b = new Date(`${to}T12:00:00`);
  return from === to ? monthDay(a) : t('healthWeekBucket', { start: monthDay(a), endDay: integer(b.getDate()) });
}

// bucketName is a bar's name in full for the screen reader's table.
export function bucketName(bucket: HealthPeriod['bucket'], from: string, to: string): string {
  const a = new Date(`${from}T12:00:00`);
  if (bucket === 'day') return new Intl.DateTimeFormat(locale(), { weekday: 'long', month: 'long', day: 'numeric' }).format(a);
  if (bucket === 'month') return new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric' }).format(a);
  return bucketLabel(bucket, from, to);
}

// summaryLines are what a group sees of a member's health on their sheet
// (001 FR-065), each with its icon: "Resting 52 bpm", with its day when that is
// not today, and "Slept 7h 30m (11:00 PM – 6:30 AM)".
export function summaryLines(s: HealthSummary): { icon: string; text: string }[] {
  const out: { icon: string; text: string }[] = [];
  if (s.resting_hr != null) {
    const today = new Date().toLocaleDateString('en-CA');
    const bpm = integer(s.resting_hr);
    const text = s.resting_day && s.resting_day !== today
      ? t('healthSummaryRestingOn', { bpm, day: monthDay(new Date(`${s.resting_day}T12:00:00`)) })
      : t('healthSummaryResting', { bpm });
    out.push({ icon: 'la-heartbeat', text });
  }
  if (s.sleep) {
    const text = t('healthSummarySlept', { duration: sleepTime(s.sleep.asleep_s), from: time(new Date(s.sleep.started_at)), to: time(new Date(s.sleep.ended_at)) });
    out.push({ icon: 'la-bed', text });
  }
  return out;
}

// niceMax rounds a chart's top up to a round multiple of a power of ten,
// so its gridlines land on round values.
export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const k of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (k * p >= v) return k * p;
  }
  return 10 * p;
}

// short writes a count as "2k" or "1.5k" from a thousand on, for the Typical chip.
export function short(n: number): string {
  if (n < 1000) return integer(Math.round(n));
  const k = Math.round(n / 100) / 10;
  return t('healthThousands', { n: k % 1 === 0 ? integer(k) : decimal(k, 1) });
}
